import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { useRefreshFunnel } from "./hooks";

/** Reason is mandatory, a comment too for "other" (docs/design/LostM.dc.html) */
export const LostDialog = ({
  deal,
  open,
  onOpenChange,
  onLost,
}: {
  deal: Pick<Deal, "id">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLost?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { dealLostReasons } = useConfigurationContext();
  const refresh = useRefreshFunnel();
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const needsComment = reason === "other" && !comment.trim();

  const submit = async () => {
    setSaving(true);
    try {
      await dataProvider.update("deals", {
        id: deal.id,
        data: {
          stage: "lost",
          lost_reason: reason,
          lost_comment: comment.trim() || null,
        },
        previousData: deal,
      });
      notify("crm.funnel.lost_dialog.done");
      await refresh();
      onOpenChange(false);
      onLost?.();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>{translate("crm.funnel.lost_dialog.title")}</DialogTitle>
        <DialogDescription>
          {translate("crm.funnel.lost_dialog.description")}
        </DialogDescription>
        <div role="radiogroup" className="flex flex-col gap-2">
          {dealLostReasons.map((r) => (
            <button
              key={r.value}
              type="button"
              role="radio"
              aria-checked={reason === r.value}
              onClick={() => setReason(r.value)}
              className={cn(
                "flex items-center gap-3 rounded-xl border px-4 py-3 text-left font-medium",
                reason === r.value
                  ? "border-destructive bg-destructive/10"
                  : "bg-card",
              )}
            >
              <span
                className={cn(
                  "size-5 rounded-full border-2",
                  reason === r.value
                    ? "border-destructive bg-[radial-gradient(circle,var(--destructive)_45%,transparent_50%)]"
                    : "border-muted-foreground",
                )}
              />
              {r.label}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-1 text-sm font-semibold text-muted-foreground">
          {translate("crm.funnel.lost_dialog.comment")}
          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={translate(
              "crm.funnel.lost_dialog.comment_placeholder",
            )}
          />
        </label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            disabled={!reason || needsComment || saving}
            onClick={submit}
          >
            {translate("crm.funnel.lost_dialog.submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
